import { useCallback } from 'react';

import Editor, { type EditorProps, type OnMount } from '@monaco-editor/react';
import { useTranslation } from 'react-i18next';

import { WebhookType } from '@/shared/types/webhook';
import {
  testWebhookAuthPreEnterSchemas,
  testWebhookAuthValidationSchemas,
  testWebhookPreNbaSchemas,
  testWebhookSchemas,
} from '@/util/constants';

type ValidationMarkers = Parameters<NonNullable<EditorProps['onValidate']>>[0];

interface WebhookJsonTabContentProps {
  value: string;
  onChange: (value?: string) => void;
  onValidate?: (markers: ValidationMarkers) => void;
  webhookType: WebhookType;
}

export const WebhookJsonTabContent = ({
  value,
  onChange,
  onValidate,
  webhookType,
}: WebhookJsonTabContentProps) => {
  const { t } = useTranslation();

  const getSchemasForType = () => {
    if (webhookType === WebhookType.PRE_NBA) {
      return testWebhookPreNbaSchemas;
    }
    if (webhookType === WebhookType.AUTH_PRE_ENTER) {
      return testWebhookAuthPreEnterSchemas;
    }
    if (webhookType === WebhookType.AUTH_VALIDATION) {
      return testWebhookAuthValidationSchemas;
    }
    return testWebhookSchemas;
  };

  const getEditorPathForType = () => {
    if (webhookType === WebhookType.PRE_NBA) {
      return 'webhook-pre-nba-payload.json';
    }
    if (webhookType === WebhookType.AUTH_PRE_ENTER) {
      return 'webhook-auth-pre-enter-payload.json';
    }
    if (webhookType === WebhookType.AUTH_VALIDATION) {
      return 'webhook-auth-validation-payload.json';
    }
    return 'webhook-test-payload.json';
  };

  const schemas = getSchemasForType();
  const editorPath = getEditorPathForType();

  const handleEditorDidMount = useCallback<OnMount>(
    (_, monacoInstance) => {
      if (!monacoInstance) {
        return;
      }

      monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas,
        enableSchemaRequest: false,
      });
    },
    [schemas]
  );

  const handleValidate = useCallback(
    (markers: ValidationMarkers) => {
      onValidate?.(markers);
    },
    [onValidate]
  );

  return (
    <div className="flex h-full flex-col justify-start">
      <span className="mb-2 max-w-md text-xs text-muted-foreground">
        {t('webhook.config.json_payload_helper')}
      </span>

      <div className="max-h-[390px] flex-1 overflow-hidden rounded-lg border border-border bg-background">
        <Editor
          value={value}
          language="json"
          theme="vs-dark"
          path={editorPath}
          height="100%"
          onChange={onChange}
          onMount={handleEditorDidMount}
          onValidate={handleValidate}
          options={{
            automaticLayout: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 13,
            formatOnPaste: true,
            formatOnType: true,
            tabSize: 2,
          }}
        />
      </div>
    </div>
  );
};
